if (!customElements.get('gated-products')) {
  class GatedProducts extends HTMLElement {
    constructor() {
      super();
      this.hashes = (this.dataset.codeHashes || '').split(' ').filter((hash) => hash.length > 0);
      this.rememberDays = parseInt(this.dataset.rememberDays, 10) || 0;
      this.urlParam = this.dataset.urlParam || 'access';
      this.storageKey = `theme:gated-access:${this.dataset.sectionId}`;

      this.gate = this.querySelector('[data-gate]');
      this.form = this.querySelector('[data-gate-form]');
      this.input = this.querySelector('[data-gate-input]');
      this.error = this.querySelector('[data-gate-error]');
      this.template = this.querySelector('[data-gate-template]');
    }

    connectedCallback() {
      if (this.dataset.gateDisabled !== undefined || !this.form) return;

      this.form.addEventListener('submit', this.handleSubmit.bind(this));
      this.input.addEventListener('input', this.clearError.bind(this));

      this.restoreAccess();
    }

    /**
     * Digests a code the same way Liquid does, so the plain code never has to be shipped
     * to the browser for comparison.
     * @param {string} value
     * @returns {Promise<string|null>} Lowercase hex digest, or null if unsupported.
     */
    async digest(value) {
      const normalised = value.trim().toLowerCase();
      if (normalised === '' || !window.crypto || !window.crypto.subtle) return null;

      const bytes = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalised));
      return Array.from(new Uint8Array(bytes))
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
    }

    /**
     * Unlocks on arrival for visitors who already have a valid grant stored, or who followed
     * a share link containing the code.
     */
    async restoreAccess() {
      const params = new URLSearchParams(window.location.search);
      const codeFromUrl = params.get(this.urlParam);

      if (codeFromUrl) {
        const hash = await this.digest(codeFromUrl);
        if (hash && this.hashes.includes(hash)) {
          this.storeAccess(hash);
          this.reveal();
        }

        // Drop the code from the address bar either way, so it is not shared onwards
        // through copied links, bookmarks or referrer headers.
        params.delete(this.urlParam);
        const query = params.toString();
        window.history.replaceState(
          {},
          '',
          `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
        );

        if (this.revealed) return;
      }

      const stored = this.readStoredAccess();
      if (stored && this.hashes.includes(stored)) this.reveal();
    }

    /**
     * @returns {string|null} The stored hash if the grant is still valid.
     */
    readStoredAccess() {
      try {
        const raw = window.localStorage.getItem(this.storageKey);
        if (!raw) return null;

        const grant = JSON.parse(raw);
        if (grant.expires && grant.expires < Date.now()) {
          window.localStorage.removeItem(this.storageKey);
          return null;
        }
        return grant.hash || null;
      } catch (e) {
        return null;
      }
    }

    storeAccess(hash) {
      if (this.rememberDays === 0) return;

      try {
        window.localStorage.setItem(
          this.storageKey,
          JSON.stringify({ hash, expires: Date.now() + this.rememberDays * 86400000 })
        );
      } catch (e) {
        // Private browsing or a full quota: access still applies for this page view.
      }
    }

    async handleSubmit(evt) {
      evt.preventDefault();
      if (this.busy) return;

      const value = this.input.value.trim();
      if (value === '') {
        this.showError();
        return;
      }

      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');

      const hash = await this.digest(value);

      this.busy = false;
      this.form.removeAttribute('aria-busy');

      if (hash && this.hashes.includes(hash)) {
        this.storeAccess(hash);
        this.reveal(true);
      } else {
        this.showError();
      }
    }

    showError() {
      this.error.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
      this.classList.add('gated-products--error');
      this.input.select();
    }

    clearError() {
      this.error.hidden = true;
      this.input.removeAttribute('aria-invalid');
      this.classList.remove('gated-products--error');
    }

    /**
     * Moves the product grid out of its inert template and into the page.
     * @param {boolean} focusContent - Whether to move focus, for visitors who just submitted.
     */
    reveal(focusContent = false) {
      if (this.revealed) return;
      this.revealed = true;

      if (this.template) {
        this.template.replaceWith(this.template.content.cloneNode(true));
        this.template = null;
      }

      if (this.gate) this.gate.remove();
      this.dataset.revealed = '';

      const content = this.querySelector('[data-gate-content]');
      if (content && focusContent) {
        content.setAttribute('tabindex', '-1');
        content.focus({ preventScroll: true });
      }

      this.dispatchEvent(
        new CustomEvent('on:gated-products:unlocked', { bubbles: true, detail: { section: this } })
      );
    }
  }

  customElements.define('gated-products', GatedProducts);
}
